export const LEAVE_BEHIND_SITE = 'https://harborcoparent.com';
export const LEAVE_BEHIND_DISPLAY_URL = 'harborcoparent.com';
export const LEAVE_BEHIND_FEE_WAIVER_DISPLAY = 'harborcoparent.com/fee-waivers';

export const LEAVE_BEHIND_CLERK_BLURB =
  'Harbor is a co-parenting app for timestamped messages, a shared custody calendar, expense splits, and records parents can export. One parent can start today; the other does not have to join. It is $9.99/month or $49.99/year per person, and parents who cannot afford it can request a fee waiver at harborcoparent.com/fee-waivers.';

export type LeaveBehindLanguage = 'en' | 'es';

export function leaveBehindAppUrl(language: LeaveBehindLanguage): string {
  const params = new URLSearchParams({
    utm_source: 'fcs',
    utm_medium: 'leavebehind',
    utm_content: language,
  });
  return `${LEAVE_BEHIND_SITE}/?${params.toString()}`;
}

export const LEAVE_BEHIND_PDF = {
  en: '/leave-behind/harbor-leave-behind-en.pdf',
  es: '/leave-behind/harbor-leave-behind-es.pdf',
} as const;

export const LEAVE_BEHIND_QR = {
  en: '/leave-behind/qr-en.svg',
  es: '/leave-behind/qr-es.svg',
} as const;

export type LeaveBehindCopy = {
  htmlLang: LeaveBehindLanguage;
  path: '/leave-behind' | '/leave-behind/es';
  title: string;
  description: string;
  screenEvent: 'Leave Behind Screen';
  printLabel: string;
  otherLanguageLabel: string;
  otherLanguagePath: '/leave-behind' | '/leave-behind/es';
  kicker: string;
  tagline: string;
  featuresHeading: string;
  tools: readonly [string, string, string, string];
  pricingHeading: string;
  price: string;
  startLine: string;
  feeWaiverHeading: string;
  feeWaiver: string;
  getApp: string;
  scanLabel: string;
  namedAppHeading: string;
  namedApp: string;
  disclaimer: string;
};

export const LEAVE_BEHIND_EN: LeaveBehindCopy = {
  htmlLang: 'en',
  path: '/leave-behind',
  title: 'Harbor | Co-parenting leave-behind',
  description:
    'A one-page Harbor flyer for Family Court Services waiting rooms: shared messages, calendar, expenses, and exportable records. $9.99/month or $49.99/year per person.',
  screenEvent: 'Leave Behind Screen',
  printLabel: 'Print this page',
  otherLanguageLabel: 'Spanish',
  otherLanguagePath: '/leave-behind/es',
  kicker: 'For co-parents',
  tagline: 'A shared place for the calendar, expenses, and messages.',
  featuresHeading: 'Keep the essentials in one place',
  tools: [
    'Timestamped messages',
    'Shared custody calendar',
    'Expense splits and receipts',
    'Records you can export',
  ],
  pricingHeading: 'Simple pricing',
  price: '$9.99/month or $49.99/year per person.',
  startLine: 'One parent can start today.',
  feeWaiverHeading: 'Need help paying?',
  feeWaiver:
    'If you cannot afford it, email a court fee waiver or benefits letter:',
  getApp: 'Get the app',
  scanLabel: 'Scan to learn more',
  namedAppHeading: 'Already have a court-ordered app?',
  namedApp: 'If a court order already names another app, use that app.',
  disclaimer:
    'Provided by Harbor. Not endorsed by or affiliated with the Superior Court of California, or any Family Court Services or Self-Help office.',
};

export const LEAVE_BEHIND_ES: LeaveBehindCopy = {
  htmlLang: 'es',
  path: '/leave-behind/es',
  title: 'Harbor | Folleto para copadres',
  description:
    'Folleto de una página de Harbor para salas de espera de Family Court Services: mensajes, calendario, gastos y registros que se pueden exportar. $9.99 al mes o $49.99 al año por persona.',
  screenEvent: 'Leave Behind Screen',
  printLabel: 'Imprimir esta página',
  otherLanguageLabel: 'English',
  otherLanguagePath: '/leave-behind',
  kicker: 'Para copadres',
  tagline: 'Un lugar compartido para el calendario, los gastos y los mensajes.',
  featuresHeading: 'Mantenga lo esencial en un solo lugar',
  tools: [
    'Mensajes con fecha y hora',
    'Calendario de custodia compartido',
    'División de gastos y recibos',
    'Registros que se pueden exportar',
  ],
  pricingHeading: 'Precio sencillo',
  price: '$9.99 al mes o $49.99 al año por persona.',
  startLine: 'Uno de los padres puede empezar hoy.',
  feeWaiverHeading: '¿Necesita ayuda para pagar?',
  feeWaiver:
    'Si no puede pagarlo, envíe por correo una exención de cuotas judiciales o una carta de beneficios:',
  getApp: 'Obtenga la aplicación',
  scanLabel: 'Escanee para obtener más información',
  namedAppHeading: '¿Ya tiene una aplicación ordenada por el tribunal?',
  namedApp:
    'Si una orden judicial ya nombra otra aplicación, use esa aplicación.',
  disclaimer:
    'Proporcionado por Harbor. No está respaldado por ni afiliado al Tribunal Superior de California, ni a ningún Servicio de Tribunales de Familia ni oficina de Autoayuda.',
};
